import React, { useEffect, useRef } from 'react';

import { StyledLegalContentHTML } from './LegalTemplate.styles';

export type HtmlBlockProps = {
  htmlContent: string;
  id: string;
};

export const HtmlBlock = ({ htmlContent, id }: HtmlBlockProps) => {
  // Handle html table of contents anchors
  const htmlContainerRef = useRef<HTMLDivElement>(null);
  const anchorsWithCallbacksRef = useRef<[HTMLAnchorElement, (e: Event) => void][]>([]);

  useEffect(() => {
    if (htmlContent.length === 0 || htmlContainerRef.current === null) {
      return undefined;
    }

    const htmlContainer = htmlContainerRef.current;
    const anchorsWithCallbacks = anchorsWithCallbacksRef.current;

    htmlContainer.querySelectorAll('a').forEach((anchor) => {
      const hashIndex = anchor.href.indexOf('#');

      // Skip this anchor if it does not have a hash
      if (hashIndex < 0) {
        return;
      }

      const elementId = anchor.href.substring(hashIndex);

      // Skip if element id is invalid
      if (elementId.length <= 1) {
        return;
      }

      const targetElement = htmlContainer.querySelector(elementId);

      // Skip if element with such ID was not found
      if (!targetElement) {
        return;
      }

      // Store anchors and callback for later unsubscribe
      anchorsWithCallbacks.push([
        anchor,
        (e) => {
          e.preventDefault();

          targetElement.scrollIntoView({
            behavior: 'smooth',
          });
        },
      ]);
    });

    // Subscribe for click events
    anchorsWithCallbacks.forEach(([anchor, cb]) => {
      anchor.addEventListener('click', cb);
    });

    // Unsubscribe before legalDocument updates or unmount
    return () => {
      while (anchorsWithCallbacks.length !== 0) {
        // Add Null Assertion since validity is guaranteed by `while` loop
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const [anchor, cb] = anchorsWithCallbacks.pop()!;
        anchor.removeEventListener('click', cb);
      }
    };
  }, [htmlContent]);

  return (
    <StyledLegalContentHTML
      id={id}
      ref={htmlContainerRef}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
};
